import React, {useState} from 'react'
import {TradeShowLayout} from '../../pages/tradeShow/TradeShowLayout'
import clsx from 'clsx'
import {useIntl} from 'react-intl'
import LanguageToggle from '../common/components/LanguageToggle'

const CILFLeadForm: React.FC = () => {
  const intl = useIntl()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [company, setCompany] = useState('')
  const [shipService, setShipService] = useState('') // NEW: matches SHIP_SERVC text field
  const [status, setStatus] = useState<null | 'success' | 'error'>(null)
  const [message, setMessage] = useState<string>('')
  const [loading, setLoading] = useState(false)

  const stripHtml = (s: string) => s.replace(/<[^>]*>?/gm, '').trim()

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setStatus(null)
    setMessage(intl.formatMessage({id: 'CILF2025_SUBMITTING'}))

    const base = 'https://gori.us14.list-manage.com/subscribe/post-json'
    const u = 'e02bd0c93b1e69aaf4ce4a9b5'
    const id = 'fca89ba9aa'
    const callbackName = `mcCallback_${Date.now()}_${Math.floor(Math.random() * 1e6)}`
    const params = new URLSearchParams({
      u,
      id,
      EMAIL: email,
      FULL_NAME: name,
      COMPANY: company,
      SHIP_SERVC: shipService,
      tags: '40240453',
      _: String(Date.now()), // cache buster
      c: callbackName, // JSONP callback name
    })

    const script = document.createElement('script')
    script.src = `${base}?${params.toString()}`
    script.async = true

    let done = false
    const cleanup = () => {
      if ((window as any)[callbackName]) {
        try {
          delete (window as any)[callbackName]
        } catch {
          ;(window as any)[callbackName] = undefined
        }
      }
      if (script.parentNode) script.parentNode.removeChild(script)
    }

    // Timeout handler (Mailchimp down / network blocked)
    const timeoutId = window.setTimeout(() => {
      if (done) return
      done = true
      cleanup()
      setLoading(false)
      setStatus('error')
      setMessage(intl.formatMessage({id: 'CILF2025_SUBMISSION_TIMEOUT'}))
    }, 12000)

    ;(window as any)[callbackName] = (resp: any) => {
      if (done) return
      done = true
      window.clearTimeout(timeoutId)
      cleanup()
      setLoading(false)

      if (resp?.result === 'success') {
        setStatus('success')
        setMessage(intl.formatMessage({id: 'CILF2025_SUBMISSION_SUCCESS'}))

        // Clear fields
        setCompany('')
        setName('')
        setEmail('')
        setShipService('')

        setTimeout(() => {
          setMessage('')
        }, 10000)
      } else {
        setStatus('error')
        const raw =
          typeof resp?.msg === 'string'
            ? resp.msg
            : intl.formatMessage({id: 'CILF2025_SUBMISSION_FAILED'})
        setMessage(stripHtml(raw))
      }
    }

    // In case of immediate network/script load error
    script.onerror = () => {
      if (done) return
      done = true
      window.clearTimeout(timeoutId)
      cleanup()
      setLoading(false)
      setStatus('error')
      setMessage('Network error. Please try again.')
    }

    document.body.appendChild(script)
  }

  return (
    <TradeShowLayout>
      <h1 className='card-title text-center pretendard mb-12'>
        {intl.formatMessage({id: 'CILF2025_TITLE'})}
      </h1>
      <div className='card border-radius-10 w-100'>
        <div className='card-body'>
          <form onSubmit={handleSubmit}>
            <div className='mb-6'>
              <label
                className='d-block mb-2 fw-bolder'
                htmlFor='email'
                dangerouslySetInnerHTML={{__html: intl.formatMessage({id: 'CILF2025_EMAIL'})}}
              ></label>
              <input
                id='email'
                className='form-control d-block w-100'
                type='email'
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className='mb-6'>
              <label
                className='d-block mb-2 fw-bolder'
                htmlFor='full-name'
                dangerouslySetInnerHTML={{__html: intl.formatMessage({id: 'CILF2025_NAME'})}}
              ></label>
              <input
                id='full-name'
                className='form-control d-block w-100'
                type='text'
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className='mb-6'>
              <label
                className='d-block mb-2 fw-bolder'
                htmlFor='company'
                dangerouslySetInnerHTML={{
                  __html: intl.formatMessage({id: 'CILF2025_COMPANY_NAME'}),
                }}
              ></label>
              <input
                id='company'
                className='form-control d-block w-100'
                type='text'
                required
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className='mb-10'>
              <label
                className='d-block mb-2 fw-bolder'
                dangerouslySetInnerHTML={{
                  __html: intl.formatMessage({id: 'CILF2025_SHIPPING_SERVICE'}),
                }}
              ></label>
              <div className='d-flex flex-column flex-sm-row gap-2 gap-sm-5 justify-content-between'>
                {['USPS', 'UPS', 'FedEx', 'Regional Carriers'].map((service) => (
                  <div key={service} className='form-check mb-1'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      id={`ship-service-${service}`}
                      value={service}
                      checked={shipService.split(',').includes(service)}
                      disabled={loading}
                      onChange={(e) => {
                        const selected = shipService ? shipService.split(',') : []
                        if (e.target.checked) {
                          setShipService([...selected, service].join(','))
                        } else {
                          setShipService(selected.filter((s) => s !== service).join(','))
                        }
                      }}
                    />
                    <label className='form-check-label' htmlFor={`ship-service-${service}`}>
                      {service}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <button
              type='submit'
              className='btn btn-outline btn-advanced-primary darker fs-4 text-uppercase rounded-pill d-block w-100'
              disabled={loading}
            >
              {loading
                ? intl.formatMessage({id: 'CILF2025_SUBMITTING'})
                : intl.formatMessage({id: 'SUBMIT'})}
            </button>
          </form>

          {message && (
            <h4 className={clsx('text-center mt-5 mb-0', {'text-danger': status === 'error'})}>
              {message}
            </h4>
          )}
        </div>
      </div>
      <LanguageToggle />
    </TradeShowLayout>
  )
}

export {CILFLeadForm}
export default CILFLeadForm
